const navy = "#0f2137";
const amber = "#f59e0b";
const muted = "#64748b";
const border = "#e2e8f0";
const text = "#1e293b";

const sections = [
  {
    title: "The short version",
    intro: true,
    body: "Level is a tool that helps you understand and compare contractor quotes. We use AI to analyze the information you give us and present it back to you in a clearer format. We are not contractors, lawyers, or financial advisors. We help you get informed — the decisions are always yours.",
  },
  {
    title: "1. What Level is",
    body: "Level is a software tool that helps homeowners prepare for contractor conversations, analyze and compare quotes, and research contractors they are considering hiring. Our analysis is generated using artificial intelligence — specifically, the Anthropic API — and is intended to help you ask better questions and make more informed decisions. Level is an information and analysis tool. Nothing we produce is a guarantee, a recommendation, a legal opinion, or professional advice of any kind.",
  },
  {
    title: "2. What Level is not",
    note: "This is important. Please read it.",
    list: [
      { label: "Level is not a law firm.", detail: "Nothing on this platform is legal advice. If you have a legal dispute with a contractor, consult a licensed attorney." },
      { label: "Level is not a financial advisor.", detail: "Nothing on this platform is financial advice. We do not tell you what to spend, what to invest, or whether a project is financially worthwhile." },
      { label: "Level is not a licensed contractor.", detail: "We do not evaluate the quality of work, assess structural integrity, or certify that any contractor is qualified to complete your project." },
      { label: "Level does not verify contractor credentials.", detail: "Where we provide guidance on researching contractor licenses, insurance, or reviews, we are pointing you toward publicly available information — we are not independently verifying, certifying, or endorsing any contractor." },
      { label: "Level's AI analysis is not infallible.", detail: "AI can make mistakes. It can misread a quote, miss context, or produce an output that doesn't reflect the full picture. Always use your own judgment and, where appropriate, consult a professional before making any significant decision." },
    ],
  },
  {
    title: "3. Your responsibilities",
    body: "By using Level, you agree to:",
    list: [
      { label: "Provide accurate information.", detail: "The quality of our analysis depends entirely on what you upload or enter. Garbage in, garbage out." },
      { label: "Use Level for lawful purposes only.", detail: "Do not upload confidential information belonging to others without their permission." },
      { label: "Make your own decisions.", detail: "Level gives you information. What you do with it is up to you." },
      { label: "Keep your account secure.", detail: "If you create an account, you are responsible for maintaining the confidentiality of your login credentials." },
    ],
  },
  {
    title: "4. Your data",
    list: [
      { label: "You own your data.", detail: "The quotes, project details, and information you upload to Level belong to you." },
      { label: "We do not sell your data.", detail: "We will never sell, rent, or share your personal information or project data with third parties for advertising or marketing purposes." },
      { label: "We use your data to provide the service.", detail: "When you upload a quote or project detail, that information is processed through the Anthropic API to generate your analysis. Anthropic's privacy policy governs how they handle data passed through their API." },
      { label: "We may use anonymized, aggregated data", detail: "to improve the product — for example, understanding what types of projects users analyze most. This data will never identify you personally." },
      { label: "You can delete your data.", detail: "If you want your account and associated data removed, contact us at privacy@levelfield.app and we will process your request promptly." },
    ],
  },
  {
    title: "5. Liability limitation",
    important: true,
    body: "Level provides analysis to help you make decisions. Level does not make decisions for you.",
    list: [
      { label: null, detail: "Any decision you make based on information or analysis provided by Level." },
      { label: null, detail: "Any financial loss, project overrun, contractor dispute, or other outcome resulting from your use of the platform." },
      { label: null, detail: "Errors, omissions, or inaccuracies in AI-generated analysis." },
      { label: null, detail: "Actions or failures of any contractor you hire, regardless of whether you used Level to research or evaluate them." },
      { label: null, detail: "Any outcome resulting from information you provided that was incomplete, inaccurate, or misrepresented." },
    ],
    note: "To the fullest extent permitted by law, Level's total liability to you for any claim arising from your use of this service is limited to the amount you paid us in the three months preceding the claim — or $10, whichever is greater.",
  },
  {
    title: "6. The service and pricing",
    body: "Level currently offers a free tier. Paid tiers will be introduced in the future. We will give you clear notice before any free features become paid features. We reserve the right to change our pricing and features at any time. If you are on a paid plan, we will give you at least 30 days' notice before any price changes take effect.",
  },
  {
    title: "7. Termination",
    list: [
      { label: "You can stop using Level at any time.", detail: "There is no obligation to continue." },
      { label: "We reserve the right to suspend or terminate accounts", detail: "that violate these terms, engage in abusive behavior, or use the platform in ways that harm other users or the service itself. We will make reasonable efforts to notify you before taking such action unless the violation is severe." },
    ],
  },
  {
    title: "8. Governing law",
    body: "These terms are governed by the laws of the State of New Jersey, United States. Any disputes arising from your use of Level will be subject to the jurisdiction of the courts of New Jersey.",
  },
  {
    title: "9. Changes to these terms",
    body: "We may update these terms from time to time. When we do, we will update the \"Last updated\" date at the top of this page. If we make material changes, we will notify you by email if you have an account with us. Continuing to use Level after changes are posted means you accept the updated terms.",
  },
];

export default function Terms() {
  return (
    <div style={{ fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", color: text, background: "#fff", minHeight: "100vh" }}>
      {/* Nav */}
      <nav style={{ display: "flex", alignItems: "center", padding: "18px 5%", borderBottom: `1px solid ${border}`, background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <a href="/" style={{ textDecoration: "none" }}>
          <div style={{ fontSize: "1.4rem", fontWeight: 900, color: navy, letterSpacing: 4, textTransform: "lowercase" }}>
            le<span style={{ color: amber }}>v</span>el
          </div>
          <span style={{ fontSize: "0.65rem", fontWeight: 500, color: muted, letterSpacing: "1.5px", textTransform: "uppercase", display: "block", marginTop: -2 }}>levelfield.app</span>
        </a>
      </nav>

      {/* Header */}
      <div style={{ background: navy, color: "#fff", padding: "48px 5% 40px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: amber, marginBottom: 12 }}>Legal</p>
          <h1 style={{ fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 900, letterSpacing: -1, marginBottom: 12, lineHeight: 1.1 }}>Terms of Service</h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>Effective date: May 17, 2026 · Last updated: May 17, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 5% 80px" }}>
        {sections.map((sec, i) => (
          <div key={i} style={{ marginBottom: 40, paddingBottom: 40, borderBottom: i < sections.length - 1 ? `1px solid ${border}` : "none" }}>
            {sec.intro ? (
              <div style={{ background: "#f8fafc", border: `1px solid ${border}`, borderRadius: 12, padding: "20px 22px" }}>
                <p style={{ fontSize: "0.9rem", color: muted, lineHeight: 1.8, fontStyle: "italic", margin: 0 }}>{sec.body}</p>
              </div>
            ) : (
              <>
                <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: navy, marginBottom: 14 }}>{sec.title}</h2>

                {sec.important && (
                  <div style={{ background: "#fff7ed", border: `1px solid #fed7aa`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                    <p style={{ fontSize: "0.85rem", fontWeight: 700, color: "#9a3412", margin: 0 }}>Please read this section carefully.</p>
                  </div>
                )}

                {sec.body && <p style={{ fontSize: "0.9rem", color: muted, lineHeight: 1.8, marginBottom: sec.list ? 14 : 0 }}>{sec.body}</p>}

                {sec.list && (
                  <div style={{ marginBottom: sec.note ? 14 : 0 }}>
                    {sec.list.map((item, j) => (
                      <div key={j} style={{ marginBottom: 14 }}>
                        {item.label ? (
                          <>
                            <span style={{ fontWeight: 700, color: navy }}>{item.label} </span>
                            <span style={{ color: muted, fontSize: "0.9rem", lineHeight: 1.7 }}>{item.detail}</span>
                          </>
                        ) : (
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <span style={{ color: amber, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>—</span>
                            <span style={{ color: muted, fontSize: "0.9rem", lineHeight: 1.7 }}>{item.detail}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {sec.note && <p style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7, marginTop: 10 }}>{sec.note}</p>}
              </>
            )}
          </div>
        ))}

        {/* Contact block */}
        <div style={{ background: navy, borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", marginBottom: 8 }}>Questions about these terms?</p>
          <a href="mailto:terms@levelfield.app" style={{ color: amber, fontWeight: 800, fontSize: "1rem", textDecoration: "none" }}>terms@levelfield.app</a>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginTop: 12, marginBottom: 0 }}>Level is based in New Jersey, United States.</p>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ padding: "24px 5%", borderTop: `1px solid ${border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: "1.1rem", fontWeight: 900, color: navy, letterSpacing: 4, textTransform: "lowercase" }}>le<span style={{ color: amber }}>v</span>el</div>
        </div>
        <p style={{ fontSize: "0.8rem", color: muted }}>© 2026 Level. Built for homeowners.</p>
        <p style={{ fontSize: "0.8rem", color: muted }}><a href="/privacy" style={{ color: muted }}>Privacy</a> · <a href="/terms" style={{ color: muted }}>Terms</a></p>
      </footer>
    </div>
  );
}
